import { useMemo, useState, useTransition } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { checkPrizeBond, type PrizeBondResult } from "@/lib/prize-bond.functions";
import { Loader2, PartyPopper, XCircle, Copy } from "lucide-react";

export const DENOMINATIONS: { id: number; label: string; color: string }[] = [
  { id: 100, label: "Rs. 100", color: "bg-emerald-500/15 text-emerald-500 border-emerald-500/40" },
  { id: 200, label: "Rs. 200", color: "bg-blue-500/15 text-blue-500 border-blue-500/40" },
  { id: 750, label: "Rs. 750", color: "bg-orange-500/15 text-orange-500 border-orange-500/40" },
  { id: 1500, label: "Rs. 1,500", color: "bg-rose-500/15 text-rose-500 border-rose-500/40" },
  { id: 7500, label: "Rs. 7,500", color: "bg-purple-500/15 text-purple-500 border-purple-500/40" },
  { id: 15000, label: "Rs. 15,000", color: "bg-yellow-500/15 text-yellow-500 border-yellow-500/40" },
  { id: 25000, label: "Rs. 25,000 ⭐", color: "bg-amber-600/15 text-amber-600 border-amber-600/40" },
  { id: 40000, label: "Rs. 40,000 ⭐", color: "bg-red-600/15 text-red-600 border-red-600/40" },
];

type Mode = "single" | "bulk" | "range";

export function PrizeBondChecker({ initialDenomination = 750 }: { initialDenomination?: number }) {
  const [denom, setDenom] = useState<number>(initialDenomination);
  const [mode, setMode] = useState<Mode>("single");
  const [singleNum, setSingleNum] = useState("");
  const [bulkText, setBulkText] = useState("");
  const [rangeFrom, setRangeFrom] = useState("");
  const [rangeTo, setRangeTo] = useState("");
  const [results, setResults] = useState<PrizeBondResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, startTransition] = useTransition();

  const check = useServerFn(checkPrizeBond);

  const bulkCount = useMemo(
    () => bulkText.split(/[\s,]+/).filter(Boolean).length,
    [bulkText],
  );
  const rangeCount = useMemo(() => {
    const a = parseInt(rangeFrom.replace(/\D+/g, ""), 10);
    const b = parseInt(rangeTo.replace(/\D+/g, ""), 10);
    if (!Number.isFinite(a) || !Number.isFinite(b)) return 0;
    return Math.max(0, Math.abs(b - a) + 1);
  }, [rangeFrom, rangeTo]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setResults(null);
    startTransition(async () => {
      try {
        let payload;
        if (mode === "single") payload = { denomination_id: denom, mode, numbers: [singleNum] };
        else if (mode === "bulk")
          payload = {
            denomination_id: denom,
            mode,
            numbers: bulkText.split(/[\s,]+/).filter(Boolean).slice(0, 100),
          };
        else payload = { denomination_id: denom, mode, range_from: rangeFrom, range_to: rangeTo };
        const res = await check({ data: payload });
        if (!res.ok) {
          setError(
            res.error === "rate_limit"
              ? "Bahut zyada checks — thodi der baad try karein."
              : res.error === "no_valid_numbers"
              ? "Valid bond numbers dijiye."
              : "Check karne mein masla hua. Dobara try karein.",
          );
          return;
        }
        setResults(res.results);
      } catch (err) {
        setError((err as Error).message || "Unknown error");
      }
    });
  }

  const won = (results ?? []).filter((r) => r.won);
  const lost = (results ?? []).filter((r) => !r.won);

  return (
    <div
      data-ad-layout-exclude="true"
      className="rounded-3xl border border-border bg-card/60 backdrop-blur p-5 md:p-7 shadow-xl"
    >
      <div className="mb-4">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Step 1 — Denomination chunein
        </div>
        <div className="flex flex-wrap gap-2">
          {DENOMINATIONS.map((d) => (
            <button
              key={d.id}
              type="button"
              onClick={() => setDenom(d.id)}
              aria-pressed={denom === d.id}
              className={`rounded-full border px-3 py-1.5 text-sm font-semibold transition ${
                denom === d.id ? `${d.color} ring-2 ring-offset-1 ring-offset-background` : "border-border text-muted-foreground hover:bg-muted"
              }`}
            >
              {d.label}
            </button>
          ))}
        </div>
      </div>

      <form onSubmit={onSubmit}>
        <Tabs value={mode} onValueChange={(v) => setMode(v as Mode)}>
          <TabsList className="mb-3">
            <TabsTrigger value="single">🔢 Single</TabsTrigger>
            <TabsTrigger value="bulk">📋 Bulk</TabsTrigger>
            <TabsTrigger value="range">📏 Range</TabsTrigger>
          </TabsList>
          <TabsContent value="single">
            <Input
              inputMode="numeric"
              placeholder="Bond number likhein (e.g. 123456)"
              value={singleNum}
              onChange={(e) => setSingleNum(e.target.value)}
              className="h-12 text-lg font-mono"
              maxLength={10}
            />
          </TabsContent>
          <TabsContent value="bulk">
            <Textarea
              placeholder={"Ek number per line — ya comma se separate karein\n123456\n234567\n345678"}
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              className="min-h-32 font-mono"
            />
            <div className="mt-1 text-xs text-muted-foreground">{bulkCount} / 100 numbers</div>
          </TabsContent>
          <TabsContent value="range">
            <div className="grid grid-cols-2 gap-3">
              <Input inputMode="numeric" placeholder="From" value={rangeFrom} onChange={(e) => setRangeFrom(e.target.value)} className="h-12 font-mono" />
              <Input inputMode="numeric" placeholder="To" value={rangeTo} onChange={(e) => setRangeTo(e.target.value)} className="h-12 font-mono" />
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              {rangeCount > 0 ? `You will check ${rangeCount} numbers` : "Max 500 numbers per range"}
              {rangeCount > 500 && <span className="ml-1 text-red-500">— range too large</span>}
            </div>
          </TabsContent>
        </Tabs>

        <Button
          type="submit"
          disabled={pending}
          className="mt-4 h-12 w-full bg-emerald-600 text-white text-base font-black hover:bg-emerald-700"
        >
          {pending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          {pending ? "Checking…" : "Check Karain 🔍"}
        </Button>
      </form>

      {error && (
        <div className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-500">
          {error}
        </div>
      )}

      {pending && (
        <div className="mt-6 space-y-2">
          <div className="h-14 rounded-lg bg-muted/60 animate-pulse" />
          <div className="h-14 rounded-lg bg-muted/40 animate-pulse" />
        </div>
      )}

      {results && !pending && (
        <div className="mt-6 space-y-3">
          {won.length > 0 && (
            <div>
              <div className="mb-3 flex items-center gap-2 text-sm font-bold text-emerald-600 dark:text-emerald-400 animate-fade-in">
                <PartyPopper className="h-4 w-4" /> Mubarak ho! {won.length} {won.length === 1 ? "number" : "numbers"} jeete hain 🎊
              </div>
              {won.map((r) => {
                const prizeLabel =
                  r.prize_category === 1 ? "🥇 First Prize" : r.prize_category === 2 ? "🥈 Second Prize" : "🥉 Third Prize";
                const meta: string[] = [];
                if (r.draw_number) meta.push(`Draw #${r.draw_number}`);
                if (r.draw_date) meta.push(new Date(r.draw_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }));
                if (r.draw_city) meta.push(r.draw_city);
                return (
                  <div
                    key={`w-${r.bond_number}`}
                    className="relative overflow-hidden rounded-2xl border-2 border-emerald-500/60 bg-gradient-to-br from-emerald-500/15 via-amber-400/10 to-yellow-500/15 p-5 mb-3 shadow-lg animate-scale-in"
                  >
                    <div aria-hidden className="pointer-events-none absolute -top-3 -right-3 text-5xl opacity-30 select-none">🎉</div>
                    <div aria-hidden className="pointer-events-none absolute -bottom-3 -left-3 text-5xl opacity-20 select-none">✨</div>

                    <div className="text-xs font-black uppercase tracking-widest text-emerald-700 dark:text-emerald-300">
                      🏆 Winner Confirmed
                    </div>
                    <div className="mt-1 text-lg font-bold text-foreground">
                      Bond <span className="font-mono">#{r.bond_number}</span>
                    </div>
                    <div className="mt-2 flex items-baseline gap-2 flex-wrap">
                      <span className="text-2xl">💰</span>
                      <span className="text-3xl md:text-4xl font-black bg-gradient-to-r from-emerald-600 via-amber-500 to-yellow-500 bg-clip-text text-transparent">
                        Rs. {r.prize_amount?.toLocaleString("en-PK") ?? "—"}
                      </span>
                      <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">jeeta! 🎁</span>
                    </div>
                    <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-background/70 backdrop-blur px-2.5 py-1 text-xs font-bold text-emerald-700 dark:text-emerald-300 border border-emerald-500/30">
                      {prizeLabel}
                    </div>
                    {meta.length > 0 && (
                      <div className="mt-2 text-xs text-muted-foreground">
                        {meta.join(" · ")}
                      </div>
                    )}
                    <div className="mt-3 flex gap-2 flex-wrap">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard?.writeText(
                            `🎉 Bond #${r.bond_number} — Rs. ${r.prize_amount?.toLocaleString("en-PK")} jeeta!${meta.length ? " (" + meta.join(", ") + ")" : ""}`,
                          );
                        }}
                      >
                        <Copy className="mr-1 h-3 w-3" /> Copy details
                      </Button>
                      <a href="#how-to-claim" className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 underline self-center">
                        Claim guide ↓
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {lost.length > 0 && (
            <div className="rounded-2xl border border-border bg-muted/30 p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
                <XCircle className="h-4 w-4" /> {lost.length} {lost.length === 1 ? "number" : "numbers"} — is baar nahi
              </div>
              {lost.length <= 20 && (
                <div className="mt-2 flex flex-wrap gap-1 font-mono text-xs text-muted-foreground">
                  {lost.map((r) => (
                    <span key={`l-${r.bond_number}`} className="rounded bg-background/60 px-1.5 py-0.5 border border-border">
                      {r.bond_number}
                    </span>
                  ))}
                </div>
              )}
              <div className="mt-2 text-xs text-muted-foreground">
                Next draw dates ke liye <a href="#draw-schedule" className="text-emerald-600 underline">schedule dekhein</a>.
              </div>
            </div>
          )}
          {won.length === 0 && lost.length === 0 && (
            <div className="text-sm text-muted-foreground">Koi results nahi mile.</div>
          )}
        </div>
      )}
    </div>
  );
}